import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Select,
  Progress,
  Tag,
  Spin,
  message,
} from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import apiService from '../../services/api';
import { useChannels } from '../../hooks/useChannels';
import { COLOR_PRIMARY } from '../../utils/colors';

const { Title, Text } = Typography;
const { Option } = Select;

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

const RouteCorridor = () => {
  const [loading, setLoading] = useState(true);
  const [corridors, setCorridors] = useState([]);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [channel, setChannel] = useState('');
  const { channels } = useChannels({ activeOnly: true });

  useEffect(() => {
    setLoading(true);
    const params = { year };
    if (channel) params.channel = channel;
    apiService.reports.getRouteCorridor(params)
      .then(res => setCorridors(res.data.corridors || []))
      .catch(() => message.error('Failed to load route corridors'))
      .finally(() => setLoading(false));
  }, [year, channel]);

  const maxCount = corridors[0]?.count || 1;

  const columns = [
    {
      title: '#',
      key: 'rank',
      width: 48,
      render: (_, __, index) => (
        <Text type="secondary" style={{ fontWeight: 600 }}>{index + 1}</Text>
      ),
    },
    {
      title: 'Collection',
      dataIndex: 'from',
      key: 'from',
      render: val => <Tag color="blue" style={{ fontFamily: 'monospace', fontSize: 13 }}>{val}</Tag>,
    },
    {
      title: '',
      key: 'arrow',
      width: 32,
      render: () => <ArrowRightOutlined style={{ color: '#999' }} />,
    },
    {
      title: 'Delivery',
      dataIndex: 'to',
      key: 'to',
      render: val => <Tag color="orange" style={{ fontFamily: 'monospace', fontSize: 13 }}>{val}</Tag>,
    },
    {
      title: 'Jobs',
      dataIndex: 'count',
      key: 'count',
      width: 60,
      render: val => <Text strong>{val}</Text>,
    },
    {
      title: 'Frequency',
      key: 'bar',
      render: (_, row) => (
        <Progress
          percent={Math.round((row.count / maxCount) * 100)}
          showInfo={false}
          strokeColor={COLOR_PRIMARY}
          style={{ marginBottom: 0 }}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 4 }}>Route Corridors</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Most common collection → delivery postcode district pairs
      </Text>

      <Card>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <Text strong>Filters:</Text>
          <Select value={year} onChange={setYear} style={{ width: 90 }}>
            {YEAR_OPTIONS.map(y => <Option key={y} value={y}>{y}</Option>)}
          </Select>
          <Select value={channel} onChange={setChannel} style={{ width: 180 }} placeholder="All Channels">
            <Option value="">All Channels</Option>
            {channels.map(c => <Option key={c._id} value={c.name}>{c.name}</Option>)}
          </Select>
          {!loading && (
            <Text type="secondary" style={{ marginLeft: 'auto' }}>
              Top {corridors.length} routes
            </Text>
          )}
        </div>

        <Spin spinning={loading}>
          <Table
            dataSource={corridors}
            columns={columns}
            rowKey={(row, i) => `${row.from}-${row.to}-${i}`}
            pagination={false}
            size="middle"
          />
        </Spin>
      </Card>
    </div>
  );
};

export default RouteCorridor;
